import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error("Could not send reset email — try again");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A] text-white">
      <div className="hidden md:block relative overflow-hidden border-r border-white/10">
        <img
          src="https://images.pexels.com/photos/35688546/pexels-photo-35688546.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/70 to-black" />
        <div className="relative p-12 flex flex-col h-full">
          <Link to="/login" className="text-xs uppercase tracking-[0.3em] text-[#38BDF8] font-bold flex items-center gap-2" data-testid="back-login-link">
            <ArrowLeft size={14} /> Back to login
          </Link>
          <div className="mt-auto">
            <h2 className="font-display text-5xl font-black uppercase tracking-tighter leading-[0.9]">
              Reset your<br />
              <span className="text-[#38BDF8]">password.</span>
            </h2>
            <p className="text-[#A3A3A3] mt-6 max-w-sm">
              Drop in your email — we'll send you a one-time reset link that expires in 30 minutes.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md" data-testid="forgot-password-page">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Recovery</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-3">Forgot password?</h1>

          {sent ? (
            <div className="mt-8 space-y-5" data-testid="forgot-password-confirmation">
              <div className="border border-[#38BDF8]/30 bg-[#38BDF8]/5 p-5">
                <div className="inline-flex items-center gap-2 text-[#38BDF8] font-bold text-sm mb-2">
                  <Mail size={16} /> Check your inbox
                </div>
                <p className="text-sm text-white/90">
                  If <strong>{email}</strong> matches a PBCOACH.APP account, we've emailed a reset link.
                  The link expires in 30 minutes.
                </p>
              </div>
              <p className="text-xs text-[#A3A3A3]">
                Didn't get it? Check spam, or <button onClick={() => setSent(false)} className="text-[#38BDF8] hover:underline font-bold" data-testid="forgot-password-retry">try a different email</button>.
              </p>
              <Link to="/login" className="btn-secondary inline-block py-3 px-6 text-xs" data-testid="back-to-login">
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="forgot-password-form">
              <p className="text-sm text-[#A3A3A3]">
                Enter your account email. We'll send a reset link — no questions asked.
              </p>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-3 text-lg"
                  placeholder="you@example.com"
                  data-testid="forgot-password-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-4 text-sm disabled:opacity-60"
                data-testid="forgot-password-submit"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <div className="text-sm text-[#A3A3A3]">
                Remembered it? <Link to="/login" className="text-[#38BDF8] font-bold hover:underline" data-testid="forgot-login-link">Sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
